import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';

import { customerService } from '../../../services/api';
import PageLayout from '../../../components/dashboard/PageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { campaignLabel } from '../../../components/campaigns/campaignLabel';
import { CUSTOM_PERIOD, PRESET_PERIODS, toApiParams } from '../../../components/metrics/hooks/useMetricsPeriod';
import { formatDate } from '../../../utils/formatters';
import { buildCampaignColumns, CampaignThumbnail } from './columns';

const SEARCH_DEBOUNCE_MS = 400;
const INITIAL_PAGINATION = { page: 0, pageSize: 25 };
const PERIODS = [...PRESET_PERIODS, CUSTOM_PERIOD];

/**
 * Period → API params. Empty period means all-time (no filter). A custom range
 * only filters once both dates are filled in and in order; its end date goes
 * as the end of that day, since the API compares lead datetimes (`__lte`).
 */
const periodToParams = ({ period, start, end }) => {
  if (!period) return {};
  if (period !== CUSTOM_PERIOD) return toApiParams({ period });
  if (!start || !end || start > end) return {};
  return { start_date: start, end_date: `${end}T23:59:59` };
};

/**
 * Campaigns of the current customer (one per Meta ad, created automatically
 * from the first CTWA message), read-only. Server-side: campaigns pile up over
 * time, so search, sorting, period and pagination all go to the API.
 */
export default function CampaignsList() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [paginationModel, setPaginationModel] = useState(INITIAL_PAGINATION);
  const [sortModel, setSortModel] = useState([{ field: 'created_at', sort: 'desc' }]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [period, setPeriod] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const resetPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));
  const updateSearch = (value) => {
    setSearch(value);
    resetPage();
  };
  const updatePeriod = (value) => {
    setPeriod(value);
    if (value !== CUSTOM_PERIOD) {
      setCustomStart('');
      setCustomEnd('');
    }
    resetPage();
  };
  const updateCustomDates = (start, end) => {
    setCustomStart(start);
    setCustomEnd(end);
    resetPage();
  };
  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    updatePeriod('');
  };

  // Recomputed only when the period changes, so "today" stays stable in the queryKey.
  const periodParams = useMemo(
    () => periodToParams({ period, start: customStart, end: customEnd }),
    [period, customStart, customEnd],
  );
  const periodApplied = Object.keys(periodParams).length > 0;
  const invalidRange = Boolean(customStart && customEnd && customStart > customEnd);
  const queryOptions = {
    paginationModel,
    sortModel,
    search: debouncedSearch,
    filterModel: {
      items: Object.entries(periodParams).map(([field, value]) => ({ field, operator: '=', value })),
    },
  };

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['campaigns', queryOptions],
    queryFn: () => customerService.getCampaigns(queryOptions).then((res) => res.data),
    placeholderData: keepPreviousData,
    retry: retryOnServerError,
  });

  const campaigns = data?.results ?? [];
  const count = data?.count ?? 0;
  const columns = useMemo(() => buildCampaignColumns({ t, language: i18n.language }), [t, i18n.language]);

  const chips = [];
  if (periodApplied) {
    const periodLabel = period === CUSTOM_PERIOD
      ? t('metrics.period.range', {
        // Plain yyyy-MM-dd dates: format in UTC so they don't shift a day back.
        start: formatDate(customStart, i18n.language, { timeZone: 'UTC' }),
        end: formatDate(customEnd, i18n.language, { timeZone: 'UTC' }),
      })
      : t(`metrics.period.${period}`);
    chips.push({
      key: 'period',
      label: `${t('campaigns.filters.period')}: ${periodLabel}`,
      onDelete: () => updatePeriod(''),
    });
  }
  if (search) {
    chips.push({
      key: 'search',
      label: `${t('campaigns.filters.searchChip')}: ${search}`,
      onDelete: () => updateSearch(''),
    });
  }

  // `ListFilterToolbar` is passed to `slots.toolbar` as a stable reference;
  // everything dynamic goes through this plain props object.
  const toolbarProps = {
    filters: [
      {
        field: 'period',
        label: t('campaigns.filters.period'),
        value: period,
        onChange: updatePeriod,
        options: PERIODS.map((value) => ({ value, label: t(`metrics.period.${value}`) })),
      },
    ],
    extraFilters: period === CUSTOM_PERIOD && (
      <Stack direction="row" spacing={1} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
        <TextField
          type="date"
          label={t('metrics.period.start')}
          value={customStart}
          onChange={(event) => updateCustomDates(event.target.value, customEnd)}
          error={invalidRange}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1 }}
        />
        <TextField
          type="date"
          label={t('metrics.period.end')}
          value={customEnd}
          onChange={(event) => updateCustomDates(customStart, event.target.value)}
          error={invalidRange}
          helperText={invalidRange ? t('metrics.period.invalidRange') : undefined}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1 }}
        />
      </Stack>
    ),
    search: { label: t('campaigns.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('campaigns.filters.resultsFound', { count }),
    clearLabel: t('campaigns.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
  };

  const header = (
    <PageHeader
      icon={<AdsClickIcon color="primary" />}
      title={t('campaigns.title')}
      breadcrumbs={[
        { label: t('campaigns.breadcrumb.dashboard') },
        { label: t('nav.groups.service') },
        { label: t('campaigns.breadcrumb.list') },
      ]}
    />
  );

  if (isError && error?.response?.status === 403) {
    return (
      <PageLayout header={header}>
        <PermissionDenied
          title={t('campaigns.states.permissionDeniedTitle')}
          message={t('campaigns.states.listPermissionDeniedMessage')}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout header={header}>
      <DataGridCard note={<FiltersNote t={t} />}>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {campaigns.length === 0 && !isLoading ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {campaigns.map((campaign) => (
                  <ListItemButton key={campaign.uuid} divider component={RouterLink} to={`/campaigns/${campaign.uuid}`}>
                    <ListItemAvatar>
                      <CampaignThumbnail campaign={campaign} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={campaignLabel(campaign)}
                      secondary={`${t('campaigns.list.leadsCount', { count: campaign.lead_count ?? 0 })} · ${formatDate(campaign.created_at, i18n.language)}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
            <TablePagination
              component="div"
              count={count}
              page={paginationModel.page}
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={[10, 25, 50]}
              onPageChange={(event, page) => setPaginationModel((prev) => ({ ...prev, page }))}
              onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: Number(event.target.value) })}
            />
          </Box>
        ) : (
          <DataGrid
            // The campaign column has two lines (label + ad id).
            getRowHeight={() => 'auto'}
            rows={campaigns}
            columns={columns}
            getRowId={(row) => row.uuid}
            rowCount={count}
            loading={isLoading || isFetching}
            paginationMode="server"
            sortingMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model);
              resetPage();
            }}
            // The API only filters by period (toolbar); column filters would do nothing.
            disableColumnFilter
            disableRowSelectionOnClick
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
              loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'linear-progress' },
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>
    </PageLayout>
  );
}

function FiltersNote({ t }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <HelpOutlineIcon color="secondary" fontSize="small" />
      <Typography variant="caption" color="text.secondary">
        {t('campaigns.filters.help.scope')}{' '}
        <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
          {t('campaigns.filters.period')}:
        </Box>{' '}
        {t('campaigns.filters.help.period')}
      </Typography>
    </Stack>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('campaigns.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('campaigns.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
